import { Observable } from "rxjs";

export namespace PersonaTypings {
  export enum PersonaQuestionAndAnswerTypes {
    User,
    Nonprofit,
    StagedNonprofit
  }

  //  Used for Persona Service
  export interface PersonaQuestionAndAnswerTypeWithDetails {
    personaQuestionAndAnswerType: PersonaQuestionAndAnswerTypes;

    nonprofitId?: number;

    stagedNonprofitId?: number;

    userId?: number;
    nonprofitAdminRequestId?: number;
    beingReviewed: boolean;
  }

  /**
   * Used by PersonaService to know which type of scenario to build data for.
   * If no NonprofitId, StagedNonprofitId nor NAR properties, then scenario is User level based.
   */
  export interface PersonaServiceDetails {
    //  Used with below NAR properties for a nonprofit on a NAR
    //  OR
    //  Used standalone for MyNonprofits area
    nonprofitId?: number;

    //  Used with below NAR properties for a staged nonprofit on a NAR
    stagedNonprofitId?: number;

    //  Nonprofti Admin Requests (NAR)
    userId?: number;  //  user id associated with NAR
    nonprofitAdminRequestId?: number; //  NAR id
    beingReviewed: boolean; //  if NAR is being reviewed

    //  Used when a user enters the site/claims org on creation
    //  Can be set with Nonprofit Id is user administers 1 nonprofit
    unansweredQuestionsOnly: boolean;
  }

  //  Used for Persona Service
  export interface UpdatePersonaQuestionsAndAnswersDataInfo {
    systemQuestionId: number;
    systemAnswerInfos: { [key: number]: number };
    customResponse?: string;
    userSystemAnswerIdForCustomResponse?: number;
    systemAnswerIdForCustomResponse?: number;
    defaultIfNoAnswer: boolean;
  };

  //  Used for Persona Service
  export interface HandlePersonaQuestionAndAnswersData {
    personaQuestionAndAnswerType: PersonaTypings.PersonaQuestionAndAnswerTypes;
    personaServiceDetails: PersonaTypings.PersonaServiceDetails;
    personaQuestionsAndAnswers: Observable<PersonaTypings.GetPersonaQuestionsAndAnswersQueryDto>;
    personaUserAnswersOne: Observable<any>;
    personaUserAnswersTwo?: Observable<any>;
  };

  //  Used for Persona Service
  export interface RemovePersonaUserAnswerForMultiResponseQuestion {
    existingAnswers: {
      systemAnswerId: number;
      userSystemAnswerId: number;
      userSystemAnswerCustomResponse: string;
    }[];
    incomingAnswerIds: number[];
    personaQuestionsAndAnswers: PersonaTypings.PersonaQuestionAndAnswers;
    allowEmptySelection: boolean;
    personaQuestionAndAnswerTypeWithDetails: PersonaTypings.PersonaQuestionAndAnswerTypeWithDetails;
  }

  //  User Has Unanswered Questions
  export interface GetDoesUserHaveUnansweredQuestionsQueryDto {
    userHasUnansweredQuestions: boolean;
  }
  //

  //  Questions and Answers
  export interface GetPersonaQuestionsAndAnswersQueryDto {
    personaQuestionsAndAnswers: PersonaQuestionAndAnswers[];
  }

  export interface PersonaQuestionAndAnswers {
      systemQuestionId: number;
      userSystemAnswerIds: number[];
      requiresAnswer: boolean;
      allowsMultipleResponses: boolean;
      translationTextKey: string;
      translationText: string;
      descriptionTranslationTextKey: string;
      descriptionTranslationText: string;
      systemAnswers: PersonaQuestionAnswers[];
  }

  export interface PersonaQuestionAnswers {
      systemAnswerId: number;
      isCustomResponse: boolean;
      translationTextKey: string;
      translationText: string;
  }
  //

  //  User Unanswered Questoins
  export interface GetUsersUnansweredQuestionsQueryDto {
    usersUnansweredQuestions: number[];
  }
  //

  //  User Answers
  export interface GetPersonaUserAnswersQueryDto {
    personaUserAnswers: PersonaUserAnswers[];
  }

  export interface PersonaUserAnswers {
      systemQuestionId: number;
      systemAnswers: PersonaUserAnswerSystemQuestionAnswers[];
  }

  export interface PersonaUserAnswerSystemQuestionAnswers {
      systemAnswerId: number;
      userSystemAnswerId: number;
      userSystemAnswerCustomResponse: string;
  }
  //

  //  User Nonprofit Answers
  export interface GetPersonaUserNonprofitAnswersQueryDto {
    personaUserNonprofitAnswers: PersonaUserNonprofitAnswers[];
  }

  export interface PersonaUserNonprofitAnswers {
      systemQuestionId: number;
      systemAnswers: PersonaUserNonprofitAnswerSystemQuestionAnswers[];
  }

  export interface PersonaUserNonprofitAnswerSystemQuestionAnswers {
      systemAnswerId: number;
      userSystemAnswerId: number;
      userSystemAnswerCustomResponse: string;
  }
  //

  //  User Staged Nonprofit Answers
  export interface GetPersonaUserStagedNonprofitAnswersQueryDto {
    personaUserStagedNonprofitAnswers: PersonaUserStagedNonprofitAnswers[];
  }

  export interface PersonaUserStagedNonprofitAnswers {
      systemQuestionId: number;
      systemAnswers: PersonaUserStagedNonprofitAnswerSystemQuestionAnswers[];
  }

  export interface PersonaUserStagedNonprofitAnswerSystemQuestionAnswers {
      systemAnswerId: number;
      userSystemAnswerId: number;
      userSystemAnswerCustomResponse: string;
  }
  //

  //  User Nonprofit Answers for Review
  export interface GetPersonaUserNonprofitAnswersForReviewQueryDto {
    personaUserNonprofitAnswersForReview: PersonaUserNonprofitAnswersForReview[];
  }

  export interface PersonaUserNonprofitAnswersForReview {
      systemQuestionId: number;
      systemAnswers: PersonaUserNonprofitAnswerForReviewSystemQuestionAnswers[];
  }

  export interface PersonaUserNonprofitAnswerForReviewSystemQuestionAnswers {
      systemAnswerId: number;
      userSystemAnswerId: number;
      userSystemAnswerCustomResponse: string;
  }
  //

  //  User Staged Nonprofit Answers for Review
  export interface GetPersonaUserStagedNonprofitAnswersForReviewQueryDto {
    personaUserStagedNonprofitAnswersForReview: PersonaUserStagedNonprofitAnswersForReview[];
  }

  export interface PersonaUserStagedNonprofitAnswersForReview {
      systemQuestionId: number;
      systemAnswers: PersonaUserStagedNonprofitAnswerForReviewSystemQuestionAnswers[];
  }

  export interface PersonaUserStagedNonprofitAnswerForReviewSystemQuestionAnswers {
      systemAnswerId: number;
      userSystemAnswerId: number;
      userSystemAnswerCustomResponse: string;
  }
  //

  //  POST Persona Answers
  export interface NewPersonaAnswer {
    personaUserAnswerId?: number;
    personaUserNonprofitAnswerId?: number;
    personaUserStagedNonprofitAnswerId?: number;
  }

}
